import { useEffect, useState } from 'react';
import { SettingsAccordion } from '../../SettingsAccordion';
import { useDebouncedCallback } from 'use-debounce';
import {
  CSS_CLASSES,
  CSS_PROPERTIES,
  INSPECTOR_DEBOUNCE_DELAY,
} from '../../constants';
import { Divider, List, Slider, Thumbnail } from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import { IconCleanCard, IconWidgets } from '@bynder/icons';
import { useCardWidgetMutations } from '../../../../../mutations/widgets/card';
import { useActiveWidget } from '../../../../../queries/guides';
import { CardWidget, WidgetAlignment } from '../../../../../types';
import AlignmentSettings from '../../AlignmentSettings';
import { useCustomCSSEditor } from '../../../../../hooks/useCustomCSSEditor';
import CardBorderRadius from './CardBorderRadius';
import CardDescription from './CardDescription';
import CardTitle from './CardTitle';

const CARD_MIN_SIZE = 128;
const CARD_MAX_SIZE = 1024;

export const CardsInWidgetSettings = () => {
  const { editWidgetStyle } = useCardWidgetMutations();
  const { translate } = useTranslate();
  const activeWidget = useActiveWidget() as CardWidget;

  const { addValueToStyle, getValueFromStyle } = useCustomCSSEditor(
    activeWidget?.style.stylesheet,
  );

  const handleStyleChange = (newStyle: string) => {
    editWidgetStyle.mutate({ newStyle });
  };

  const handleWidgetResize = (size: number) => {
    const newStyle = addValueToStyle(
      `${size}`,
      CSS_PROPERTIES.WIDTH,
      CSS_CLASSES.CARD,
    );
    editWidgetStyle.mutate({ newStyle });
  };

  const handleWidgetAlignmentChange = (
    value: string,
    newAlignment: WidgetAlignment,
  ) => {
    const newStyle = addValueToStyle(newAlignment, value);
    editWidgetStyle.mutate({ newStyle });
  };
  return (
    <SettingsAccordion
      thumbnail={<Thumbnail icon={<IconWidgets />} />}
      subtext={translate('WIDGET_INSPECTOR.CARD_GRID_SETTINGS_SUBTEXT')}
      title={translate('WIDGET_INSPECTOR.CARD_GRID_SETTINGS_ACCORDION')}
    >
      <CardSizeSlider
        size={
          getValueFromStyle(CSS_PROPERTIES.WIDTH, CSS_CLASSES.CARD) ||
          activeWidget?.style.size
        }
        handleResize={handleWidgetResize}
      />

      <Divider />
      <AlignmentSettings
        label={translate('WIDGET_INSPECTOR.CARD_ALIGNMENT')}
        alignment={
          getValueFromStyle(CSS_PROPERTIES.JUSTIFY_CONTENT) ||
          activeWidget?.style.alignment
        }
        handleChange={handleWidgetAlignmentChange}
      />
      <Divider />

      <CardTitle
        style={activeWidget.style.stylesheet}
        handleStyleChange={handleStyleChange}
      />
      <Divider />
      <CardDescription
        style={activeWidget.style.stylesheet}
        handleStyleChange={handleStyleChange}
      />
      <Divider />
      <CardBorderRadius
        style={activeWidget.style.stylesheet}
        handleStyleChange={handleStyleChange}
      />
    </SettingsAccordion>
  );
};

const CardSizeSlider = ({
  size,
  handleResize,
}: {
  size: number;
  handleResize: (size: number) => void;
}) => {
  const [inputValue, setInputValue] = useState(size);
  const onSetSize = (newSize: number) => {
    return handleResize(newSize);
  };

  const debouncedSetTitle = useDebouncedCallback(
    onSetSize,
    INSPECTOR_DEBOUNCE_DELAY,
  );
  const onSliderValueChanged = (value: number) => {
    setInputValue(value);
    debouncedSetTitle(value);
  };
  useEffect(() => {
    setInputValue(size);
  }, [size]);

  return (
    <>
      <List.Item>
        <Translate id="WIDGET_INSPECTOR.CARD_WIDTH" />
      </List.Item>
      <Slider
        data-testid="cardSize"
        min={CARD_MIN_SIZE}
        max={CARD_MAX_SIZE}
        iconLeft={<IconCleanCard size="20" />}
        value={inputValue}
        onChange={onSliderValueChanged}
        inputPosition="right"
      />
    </>
  );
};
